import ExcelJS from "exceljs";
import fileDownload from "js-file-download";
import { formatProducerName } from "utility/formatters";

const EXPORT_COLUMNS = [
  { header: "Producer", getValue: (row) => formatProducerName(row) },
  { header: "Commodity", field: "commodity_name" },
  { header: "Production", field: "production", numFmt: "#,##0" },
  { header: "Guarantee", field: "production_guarantee", numFmt: "#,##0" },
  { header: "Goal", field: "marketing_goal_units", numFmt: "#,##0" },
  { header: "Hedged", field: "unit_quantity_hedge_total", numFmt: "#,##0" },
  { header: "% Protected", field: "calc_percent_protected", numFmt: '[>10]"∞";[<-10]"-∞";0.00%' }, // conditional format: cell stays numeric so Excel sorts by the real value
  { header: "Cash", field: "amount_cash", numFmt: "#,##0" },
  { header: "Basis", field: "amount_basis_only", numFmt: "#,##0" },
  { header: "HTA", field: "amount_hta", numFmt: "#,##0" },
  { header: "Futures", field: "unit_quantity_futures", numFmt: "#,##0" },
  { header: "Long Puts", field: "unit_quantity_long_put", numFmt: "#,##0" },
  { header: "OTC", field: "unit_quantity_otc", numFmt: "#,##0" },
  { header: "Long Calls", field: "unit_quantity_long_call", numFmt: "#,##0" },
];

function sanitizeFilePart(value) {
  return String(value ?? "")
    .trim()
    .replace(/[^\w.-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export async function exportRiskSummaryBrokerWorkbook({ rows, year, commodityName, brokerLabel }) {
  if (!rows?.length) {
    throw new Error("No rows to export.");
  }

  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Tilley";
  const sheet = workbook.addWorksheet("Position Summary");

  if (year != null && year !== "") {
    sheet.addRow(["Year", year]);
  }
  if (commodityName) {
    sheet.addRow(["Commodity", commodityName]);
  }
  if (brokerLabel) {
    sheet.addRow(["Broker(s)", brokerLabel]);
  }
  if (year != null || commodityName || brokerLabel) {
    sheet.addRow([]);
  }

  const headerRow = sheet.addRow(EXPORT_COLUMNS.map((col) => col.header));
  headerRow.font = { bold: true };
  headerRow.alignment = { vertical: "middle" };

  rows.forEach((row) => {
    const values = EXPORT_COLUMNS.map((col) => {
      const raw = col.getValue ? col.getValue(row) : row?.[col.field];
      return raw ?? "";
    });
    const dataRow = sheet.addRow(values);
    EXPORT_COLUMNS.forEach((col, index) => {
      if (!col.numFmt) return;
      const cell = dataRow.getCell(index + 1);
      cell.numFmt = col.numFmt;
    });
  });

  sheet.columns.forEach((column, index) => {
    const header = EXPORT_COLUMNS[index]?.header ?? "";
    let maxLength = header.length;
    column.eachCell({ includeEmpty: false }, (cell) => {
      const cellLength = cell.value == null ? 0 : String(cell.value).length;
      maxLength = Math.max(maxLength, cellLength);
    });
    column.width = Math.min(Math.max(maxLength + 2, 10), 48);
  });

  sheet.views = [{ state: "frozen", ySplit: headerRow.number }];

  const yearPart = sanitizeFilePart(year) || "year";
  const commodityPart = commodityName ? `-${sanitizeFilePart(commodityName)}` : "";
  const buffer = await workbook.xlsx.writeBuffer();
  fileDownload(buffer, `Position-Summary-Broker-${yearPart}${commodityPart}.xlsx`);
}
